import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, ClipboardList, FileDown, FileOutput, FileText, NotebookPen, Settings, Sparkles, UserPlus, Users } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useUsageLimits } from '../hooks/useUsageLimits';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  userName?: string;
  onUpgrade?: () => void;
}

export default function WelcomeModal({ isOpen, onClose, onComplete, userName, onUpgrade }: WelcomeModalProps) {
  const { t } = useTranslation(['app', 'common']);
  const [currentStep, setCurrentStep] = useState(0);
  const { subscriptionTier } = useUsageLimits();
  const { containerRef } = useFocusTrap<HTMLDivElement>({
    isActive: isOpen,
    onEscape: onClose,
    closeOnEscape: true,
  });

  if (!isOpen) return null;

  const isFreePlan = subscriptionTier === 'free';
  const isProPlan = subscriptionTier === 'pro';
  const planLabel = isFreePlan ? 'Free' : (isProPlan ? 'Pro' : 'Enterprise');
  const planBadge = isFreePlan
    ? { headline: '5', sub: t('app:welcomeModal.testsPerMonth') }
    : (isProPlan ? { headline: 'PRO', sub: t('app:welcomeModal.unlimited') } : { headline: 'ENT', sub: t('app:welcomeModal.custom') });

  const steps = [
    {
      title: userName ? t('app:welcomeModal.welcomeWithName', { name: userName }) : t('app:welcomeModal.welcome'),
      description: t('app:welcomeModal.description'),
      image: (
        <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-50 flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('app:welcomeModal.withPsymetrika')}
          </p>
          <ul className="space-y-3 text-left">
            {[
              { text: t('app:welcomeModal.features.applyInstruments'), icon: FileText, tone: 'bg-emerald-50 text-emerald-700' },
              { text: t('app:welcomeModal.features.generateAnalysis'), icon: Sparkles, tone: 'bg-amber-50 text-amber-700' },
              { text: t('app:welcomeModal.features.recordEvolutivos'), icon: Users, tone: 'bg-sky-50 text-sky-700' },
              { text: t('app:welcomeModal.features.centralizeHistory'), icon: BarChart3, tone: 'bg-violet-50 text-violet-700' },
              { text: t('app:welcomeModal.features.exportReports'), icon: FileDown, tone: 'bg-rose-50 text-rose-700' },
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg ${item.tone} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <item.icon className="w-4 h-4" />
                </div>
                <span className="text-gray-700">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      title: t('app:welcomeModal.currentPlan', { plan: planLabel }),
      description: isFreePlan
        ? t('app:welcomeModal.freeDescription')
        : t('app:welcomeModal.proDescription'),
      image: (
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white">
          <div className="text-center">
            <div className="text-2xl font-bold">{planBadge.headline}</div>
            <div className="text-xs">{planBadge.sub}</div>
          </div>
        </div>
      ),
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t('app:welcomeModal.planIncludes', { plan: planLabel })}</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              {(isFreePlan
                ? [
                    t('app:welcomeModal.freePlanFeatures.testsPerMonth'),
                    t('app:welcomeModal.freePlanFeatures.oneAnalysis'),
                    t('app:welcomeModal.freePlanFeatures.accessTests'),
                    t('app:welcomeModal.freePlanFeatures.pdfExport'),
                  ]
                : [
                    t('app:welcomeModal.proPlanFeatures.unlimitedTests'),
                    t('app:welcomeModal.proPlanFeatures.fullCatalog'),
                    t('app:welcomeModal.proPlanFeatures.reanalysis'),
                    t('app:welcomeModal.proPlanFeatures.advancedExport'),
                  ]
              ).map((item, index) => (
                <li key={index} className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {isFreePlan ? (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2">{t('app:welcomeModal.needMore')}</h4>
              <p className="text-sm text-purple-800 mb-3">
                {t('app:welcomeModal.proIncludes')}
              </p>
              <div className="flex items-center justify-between gap-3">
                <p className="text-2xl font-bold text-purple-900">{t('app:welcomeModal.proPrice')}</p>
                <button
                  onClick={() => {
                    onUpgrade?.();
                    onClose();
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
                >
                  {t('app:welcomeModal.viewProPlan')}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-900 mb-1">{t('app:welcomeModal.activePlan')}</h4>
              <p className="text-sm text-emerald-800">
                {t('app:welcomeModal.activeDescription', { plan: planLabel })}
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('app:welcomeModal.firstSteps'),
      description: t('app:welcomeModal.firstStepsDescription'),
      image: (
        <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-50 flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            {[
              {
                step: '1',
                title: t('app:welcomeModal.steps.configureProfile.title'),
                description: t('app:welcomeModal.steps.configureProfile.description'),
                icon: Settings,
                tone: 'bg-slate-100 text-slate-700',
                badge: 'bg-slate-700 text-white',
              },
              {
                step: '2',
                title: t('app:welcomeModal.steps.addPatient.title'),
                description: t('app:welcomeModal.steps.addPatient.description'),
                icon: UserPlus,
                tone: 'bg-blue-50 text-blue-700',
                badge: 'bg-blue-700 text-white',
              },
              {
                step: '3',
                title: t('app:welcomeModal.steps.applyTest.title'),
                description: t('app:welcomeModal.steps.applyTest.description'),
                icon: ClipboardList,
                tone: 'bg-indigo-50 text-indigo-700',
                badge: 'bg-indigo-700 text-white',
              },
              {
                step: '4',
                title: t('app:welcomeModal.steps.recordEvolutivo.title'),
                description: t('app:welcomeModal.steps.recordEvolutivo.description'),
                icon: NotebookPen,
                tone: 'bg-amber-50 text-amber-700',
                badge: 'bg-amber-700 text-white',
              },
              {
                step: '5',
                title: t('app:welcomeModal.steps.generateReport.title'),
                description: t('app:welcomeModal.steps.generateReport.description'),
                icon: FileOutput,
                tone: 'bg-rose-50 text-rose-700',
                badge: 'bg-rose-700 text-white',
              },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="relative flex-shrink-0">
                  <div className={`w-10 h-10 rounded-xl ${item.tone} flex items-center justify-center`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full ${item.badge} text-[10px] font-semibold flex items-center justify-center shadow-sm`}>
                    {item.step}
                  </span>
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">{t('app:welcomeModal.tipTitle')}</p>
              <p className="text-xs text-yellow-800 mt-1">
                {t('app:welcomeModal.tipDescription')}
              </p>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 lg:px-8 py-5 lg:py-6 flex items-center justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0 hidden sm:flex">
              {currentStepData.image}
            </div>
            <div className="min-w-0">
              <h2 id="welcome-modal-title" className="text-xl lg:text-2xl font-bold text-gray-900">{currentStepData.title}</h2>
              <p className="text-gray-600 mt-1">{currentStepData.description}</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-8 py-8">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-8 py-6">
          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                {t('app:welcomeModal.stepOf', { current: currentStep + 1, total: steps.length })}
              </span>
              <button
                onClick={handleSkip}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {t('app:welcomeModal.skipTutorial')}
              </button>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                currentStep === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('app:welcomeModal.previous')}
            </button>

            <button
              onClick={handleNext}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              {currentStep === steps.length - 1 ? t('app:welcomeModal.letsStart') : t('app:welcomeModal.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
